import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

interface ApprovalHistory {
  approver: string;
  status: "approved" | "rejected";
  comments: string;
  _id: string;
  timestamp: string;
}

interface Request {
  _id: string;
  orderId: string;
  requestId: string;
  itemName: string;
  qualityType: string;
  quantity: number;
  location: string;
  pricePerUnit?: number;
  estimatedPrice?: number;
  role: string;
  status:
    | "pending"
    | "in_progress"
    | "completed"
    | "rejected"
    | "delivered"
    | "settled";
  currentApprover: string;
  createdBy: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    role: string;
  };
  approvalHistory: ApprovalHistory[];
  createdAt: string;
  updatedAt: string;
  deliveryImages?: Array<{
    url: string;
    timestamp: string;
    uploadedAt: string;
    _id: string;
  }>;
}

/**
 * Generates a PDF document for a request's approval history
 */
export const generateApprovalHistoryPDF = async (request: Request) => {
  const doc = new jsPDF();

  // Add title
  doc.setFontSize(18);
  doc.text(`PO #${request.orderId || request._id.substring(0, 8)}`, 14, 20);
  doc.text(`REQUEST ${request.status.toUpperCase().replace("_", " ")}`, 14, 30);

  // Add request details
  doc.setFontSize(11);
  doc.text(`PO Number: ${request.orderId || "N/A"}`, 14, 40);
  doc.text(`Request ID: ${request._id}`, 14, 46);
  doc.text(`Item: ${request.itemName}`, 14, 52);
  doc.text(`Quality Type: ${request.qualityType}`, 14, 58);
  doc.text(`Quantity: ${request.quantity}`, 14, 64);
  doc.text(`Location: ${request.location}`, 14, 70);
  doc.text(
    `Price Per Unit: $${(request.pricePerUnit || 0).toFixed(2)}`,
    14,
    76
  );
  doc.text(
    `Estimated Price: $${(request.estimatedPrice || 0).toFixed(2)}`,
    14,
    82
  );
  doc.text(`Status: ${request.status.replace("_", " ")}`, 14, 88);
  doc.text(
    `Current Approver: ${request.currentApprover.replace("_", " ")}`,
    14,
    94
  );
  doc.text(`Created by: ${request.createdBy?.name || "Unknown"}`, 14, 100);
  doc.text(
    `Created at: ${new Date(request.createdAt).toLocaleString()}`,
    14,
    106
  );

  let yPosition = 116;

  // Add approval history table
  if (request.approvalHistory && request.approvalHistory.length > 0) {
    // Define the columns for the table
    const tableColumn = ["Approver", "Status", "Comments", "Timestamp"];
    const tableRows = request.approvalHistory.map((history) => [
      history.approver.replace("_", " "),
      history.status,
      history.comments,
      new Date(history.timestamp).toLocaleString(),
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: yPosition,
      theme: "grid",
      styles: { fontSize: 9 },
      headStyles: { fillColor: [66, 66, 66] },
      margin: { left: 14, right: 14 },
    });

    // Get the final Y position after the table
    const finalY = (doc as any).lastAutoTable.finalY;
    yPosition = finalY + 10;
  } else {
    doc.text("No approval history available.", 14, yPosition);
    yPosition += 10;
  }

  // Add delivery images if available
  if (request.deliveryImages && request.deliveryImages.length > 0) {
    // Add header for images
    doc.setFontSize(14);
    doc.text("Delivery Images", 14, yPosition);
    yPosition += 10;

    // Add each image to the PDF
    for (let i = 0; i < request.deliveryImages.length; i++) {
      try {
        // Check if we need a new page
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        const imageUrl = request.deliveryImages[i].url;

        try {
          const img = await loadImage(imageUrl);

          // Calculate dimensions to maintain aspect ratio
          const maxWidth = 180;
          const maxHeight = 100;
          const aspectRatio = img.width / img.height;

          let width = maxWidth;
          let height = width / aspectRatio;

          if (height > maxHeight) {
            height = maxHeight;
            width = height * aspectRatio;
          }

          // Add image to PDF
          doc.addImage(img, "JPEG", 14, yPosition, width, height);
          yPosition += height + 10;

          // Add image caption
          doc.setFontSize(9);
          doc.text(`Image ${i + 1}`, 14, yPosition);
          yPosition += 5;
        } catch (error) {
          console.error(`Error adding image ${i + 1}:`, error);
          // Add a placeholder for failed images
          doc.setFontSize(9);
          doc.text(`Image ${i + 1} - Failed to load`, 14, yPosition);
          doc.text(`Error: ${error.message}`, 14, yPosition + 5);
          yPosition += 15;
        }
      } catch (error) {
        console.error(`Error processing image ${i + 1}:`, error);
        yPosition += 10;
      }
    }
  }

  // Save the PDF
  doc.save(`PO_${request.orderId || request._id.substring(0, 8)}.pdf`);
};

// Helper function to load image
const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";

    // Set a timeout for image loading
    const timeout = setTimeout(() => {
      img.src = ""; // Cancel the current load
      reject(new Error("Image load timeout"));
    }, 10000); // 10 second timeout

    img.onload = () => {
      clearTimeout(timeout);
      resolve(img);
    };

    img.onerror = async (error) => {
      clearTimeout(timeout);
      console.error("Error loading image:", error);

      try {
        // Try to load the image with a proxy
        const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(url)}`;
        const fallbackImg = new Image();
        fallbackImg.crossOrigin = "Anonymous";

        // Set a timeout for the fallback image
        const fallbackTimeout = setTimeout(() => {
          fallbackImg.src = ""; // Cancel the current load
          reject(new Error("Fallback image load timeout"));
        }, 10000);

        fallbackImg.onload = () => {
          clearTimeout(fallbackTimeout);
          resolve(fallbackImg);
        };

        fallbackImg.onerror = (fallbackError) => {
          clearTimeout(fallbackTimeout);
          console.error("Error loading image with proxy:", fallbackError);
          reject(new Error("Failed to load image"));
        };

        fallbackImg.src = proxyUrl;
      } catch (error) {
        reject(error);
      }
    };

    img.src = url;
  });
};

/**
 * Prints the approval history directly
 */
export const printApprovalHistory = (request: Request) => {
  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  // Generate the HTML content
  let html = `
    <html>
      <head>
        <title>PO #${request.orderId || request._id.substring(0, 8)}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 28px; margin-bottom: 10px; }
          h2 { font-size: 24px; margin-bottom: 20px; color: #555; text-transform: uppercase; }
          .details { margin-bottom: 30px; }
          .details p { margin: 5px 0; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
          tr:nth-child(even) { background-color: #f9f9f9; }
        </style>
      </head>
      <body>
        <h1>PO #${request.orderId || request._id.substring(0, 8)}</h1>
        <h2>REQUEST ${request.status.toUpperCase().replace("_", " ")}</h2>
        <div class="details">
          <p><strong>PO Number:</strong> ${request.orderId || "N/A"}</p>
          <p><strong>Request ID:</strong> ${request._id}</p>
          <p><strong>Item:</strong> ${request.itemName}</p>
          <p><strong>Quality Type:</strong> ${request.qualityType}</p>
          <p><strong>Quantity:</strong> ${request.quantity}</p>
          <p><strong>Location:</strong> ${request.location}</p>
          <p><strong>Price Per Unit:</strong> $${(
            request.pricePerUnit || 0
          ).toFixed(2)}</p>
          <p><strong>Estimated Price:</strong> $${(
            request.estimatedPrice || 0
          ).toFixed(2)}</p>
          <p><strong>Status:</strong> ${request.status.replace("_", " ")}</p>
          <p><strong>Current Approver:</strong> ${request.currentApprover.replace(
            "_",
            " "
          )}</p>
          <p><strong>Created by:</strong> ${
            request.createdBy?.name || "Unknown"
          }</p>
          <p><strong>Created at:</strong> ${new Date(
            request.createdAt
          ).toLocaleString()}</p>
        </div>
  `;

  // Add approval history table
  if (request.approvalHistory && request.approvalHistory.length > 0) {
    html += `
      <h2>Approval History</h2>
      <table>
        <thead>
          <tr>
            <th>Approver</th>
            <th>Status</th>
            <th>Comments</th>
            <th>Timestamp</th>
          </tr>
        </thead>
        <tbody>
    `;

    request.approvalHistory.forEach((history) => {
      html += `
        <tr>
          <td>${history.approver.replace("_", " ")}</td>
          <td>${history.status}</td>
          <td>${history.comments}</td>
          <td>${new Date(history.timestamp).toLocaleString()}</td>
        </tr>
      `;
    });

    html += `
        </tbody>
      </table>
    `;
  } else {
    html += `<p>No approval history available.</p>`;
  }

  html += `
      </body>
    </html>
  `;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
};

/**
 * Generates a PDF with delivery images
 */
export const generateDeliveryImagesPDF = async (request: Request) => {
  if (!request.deliveryImages || request.deliveryImages.length === 0) {
    alert("No delivery images available to download");
    return;
  }

  const doc = new jsPDF();

  // Add title
  doc.setFontSize(18);
  doc.text(`PO #${request.orderId || request._id.substring(0, 8)}`, 14, 20);
  doc.text(`REQUEST ${request.status.toUpperCase().replace("_", " ")}`, 14, 30);

  // Add request basics
  doc.setFontSize(11);
  doc.text(`PO Number: ${request.orderId || "N/A"}`, 14, 40);
  doc.text(`Request ID: ${request._id}`, 14, 46);
  doc.text(`Item: ${request.itemName}`, 14, 52);
  doc.text(`Quality Type: ${request.qualityType}`, 14, 58);
  doc.text(`Quantity: ${request.quantity}`, 14, 64);
  doc.text(`Location: ${request.location}`, 14, 70);
  doc.text(`Status: ${request.status.replace("_", " ")}`, 14, 76);
  doc.text(`Created by: ${request.createdBy?.name || "Unknown"}`, 14, 82);

  let yPosition = 96;

  // Add header for images
  doc.setFontSize(14);
  doc.text("Delivery Images", 14, yPosition);
  yPosition += 10;

  // Add each image to the PDF
  for (let i = 0; i < request.deliveryImages.length; i++) {
    try {
      // Check if we need a new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      const imgUrl = request.deliveryImages[i].url;

      // Create a temporary image element to load the image
      const img = new Image();
      img.crossOrigin = "Anonymous"; // Enable CORS if the image is from a different domain

      // Convert loaded image to canvas, then to data URL
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);

        try {
          // Add the image to PDF
          const imgData = canvas.toDataURL("image/jpeg");
          doc.addImage(imgData, "JPEG", 14, yPosition, 180, 100);

          yPosition += 110;

          // If this is the last image, save the PDF
          if (i === request.deliveryImages!.length - 1) {
            doc.save(`delivery_images_${request._id.substring(0, 8)}.pdf`);
          }
        } catch (error) {
          console.error("Error adding image to PDF", error);
        }
      };

      // Set the source to load the image
      img.src = imgUrl;

      // Add a placeholder text in case image loading fails
      doc.text(`Image ${i + 1}`, 14, yPosition + 50);

      yPosition += 110;
    } catch (error) {
      console.error("Error adding image to PDF", error);
      // Continue with next image
    }
  }

  // For direct saving without waiting for image onload
  setTimeout(() => {
    doc.save(`delivery_images_${request._id.substring(0, 8)}.pdf`);
  }, 500);
};

/**
 * Prints the delivery images directly
 */
export const printDeliveryImages = (request: Request) => {
  if (!request.deliveryImages || request.deliveryImages.length === 0) {
    alert("No delivery images available to print");
    return;
  }

  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  // Generate the HTML content
  let html = `
    <html>
      <head>
        <title>Order #${request._id.substring(0, 8)}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 28px; margin-bottom: 10px; }
          h2 { font-size: 24px; margin-bottom: 20px; color: #555; text-transform: uppercase; }
          .details { margin-bottom: 30px; }
          .details p { margin: 5px 0; }
          .image-container { margin-bottom: 20px; }
          img { max-width: 100%; height: auto; border: 1px solid #ddd; }
        </style>
      </head>
      <body>
        <h1>ORDER #${request._id.substring(0, 8)}</h1>
        <h2>REQUEST ${request.status.toUpperCase().replace("_", " ")}</h2>
        <div class="details">
          <p><strong>Request ID:</strong> ${request._id}</p>
          <p><strong>Item:</strong> ${request.itemName}</p>
          <p><strong>Quality Type:</strong> ${request.qualityType}</p>
          <p><strong>Quantity:</strong> ${request.quantity}</p>
          <p><strong>Location:</strong> ${request.location}</p>
          <p><strong>Status:</strong> ${request.status.replace("_", " ")}</p>
          <p><strong>Created by:</strong> ${
            request.createdBy?.name || "Unknown"
          }</p>
        </div>
        <h2>Delivery Images</h2>
  `;

  // Add delivery images
  request.deliveryImages.forEach((image, index) => {
    const imgSrc = image.url;
    html += `
      <div class="image-container">
        <h3>Image ${index + 1}</h3>
        <img src="${imgSrc}" alt="Delivery Image ${index + 1}" />
      </div>
    `;
  });

  html += `
      </body>
    </html>
  `;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
};
